/* DRIVE IQ — BOATING DRIVE V2 REAL MAP AUTHORITY
   File: drive-iq-boating-drive.js

   Required:
   - Mapbox GL JS loaded by the Boating Drive Custom Liquid.
   - A valid Mapbox token exposed by one of:
       window.DRIVE_IQ_MAPBOX_TOKEN
       window.MAPBOX_ACCESS_TOKEN
       window.DriveIQAdventureConfig.mapbox.accessToken
       window.DriveIQConfig.mapboxToken

   Marine route contract:
   window.DRIVE_IQ_BOATING_BUILD_ROUTE(context) should return:
   {
     geometry: [[lng,lat], [lng,lat], ...]
     distanceMiles: 4.2,
     etaMinutes: 18,
     confidence: "Route Strong",
     maneuver: {
       icon: "↗",
       distanceText: "0.8 mi",
       instruction: "Bear right toward the marked channel"
     }
   }

   Safety:
   - This file never invents a navigable boating route.
   - When route geometry is absent, it draws only a dashed bearing line
     from vessel/launch to destination and labels route confidence pending.
*/

(function(window,document){
"use strict";

if(window.DriveIQBoatingDrive){
  return;
}

var BUILD="V4_1_BOAT_MARKER_IDENTITY_AUTHORITY";
var TRIP_STORAGE_KEY="driveiq.boating.trip";
var DRIVE_STATE_KEY="driveiq.boating.drive.v3";
var START_URL="/pages/boating-start";
var ROUTE_SOURCE_ID="bd-marine-route";
var ROUTE_CASING_LAYER_ID="bd-marine-route-casing";
var ROUTE_LAYER_ID="bd-marine-route-line";
var BEARING_SOURCE_ID="bd-bearing-line";
var BEARING_LAYER_ID="bd-bearing-line-layer";
var BREADCRUMB_SOURCE_ID="bd-breadcrumb-track";
var BREADCRUMB_LAYER_ID="bd-breadcrumb-track-layer";
var RETURN_SOURCE_ID="bd-return-line";
var RETURN_LAYER_ID="bd-return-line-layer";

var watchId=null;
var intelligenceTimer=null;
var routeTimer=null;
var map=null;
var vesselMarker=null;
var launchMarker=null;
var destinationMarker=null;
var discoveryMarkers=[];
var activeDiscoveryType="";
var selectedDiscoveryPlace=null;
var discoveryAbortController=null;
var launchPromptCandidate=null;
var launchDetectionDismissedAt=0;
var launchDetectionLastPromptAt=0;
var launchDetectionSamples=[];

var state={
  ready:false,
  guidanceActive:false,
  awarenessActive:false,
  returnMode:false,
  trip:null,
  route:null,
  location:null,
  breadcrumb:[],
  discoveryResults:[],
  homeBase:null,
  launchDetection:{
    prompted:false,
    dismissed:false,
    confidence:0,
    reason:""
  },
  speedMph:0,
  heading:0,
  etaText:"--",
  distanceMiles:null,
  maneuver:{
    icon:"↑",
    distanceText:"--",
    instruction:"Waiting for a marine route"
  },
  routeConfidence:"Awareness Ready",
  waterStatus:"Checking",
  selectedIntel:"location",
  cardState:"compact",
  lastError:"",
  intelligence:{
    location:{title:"Location IQ",label:"Location IQ • Active",state:"good",copy:"Your live vessel position stays visible on the map.",alerts:[]},
    home:{title:"Home Base",label:"Home Base • Checking",state:"",copy:"Your saved launch or parked location will stay highlighted.",alerts:[]},
    wind:{title:"Wind IQ",label:"Wind IQ • Checking",state:"",copy:"Wind conditions are being monitored.",alerts:[]},
    weather:{title:"Storm IQ",label:"Storm IQ • Monitoring",state:"",copy:"Drive IQ watches for meaningful weather changes.",alerts:[]},
    water:{title:"Water IQ",label:"Water IQ • Checking",state:"",copy:"Water and tide conditions are being monitored where available.",alerts:[]},
    hazards:{title:"Hazard IQ",label:"Hazard IQ • Monitoring",state:"",copy:"Drive IQ is monitoring meaningful nearby marine hazards.",alerts:[]}
  }
};

function byId(id){return document.getElementById(id);}
function clean(value){return String(value==null?"":value).replace(/\s+/g," ").trim();}
function number(value){
  if(
    value===null||
    value===undefined||
    value===""||
    (typeof value==="string"&&!value.trim())
  ){
    return null;
  }

  var n=Number(value);
  return isFinite(n)?n:null;
}
function setText(id,value){var node=byId(id);if(node){node.textContent=clean(value);}}
function show(node){if(node){node.classList.remove("bd-hidden");}}
function hide(node){if(node){node.classList.add("bd-hidden");}}

function emit(name,detail){
  try{
    document.dispatchEvent(new CustomEvent(name,{detail:detail||{}}));
  }catch(error){}
}

function callHook(name,context){
  var hook=window[name];
  if(typeof hook!=="function"){return Promise.resolve(null);}
  try{return Promise.resolve(hook(context));}
  catch(error){return Promise.reject(error);}
}

function mapboxToken(){
  return clean(
    window.DRIVE_IQ_MAPBOX_TOKEN ||
    window.MAPBOX_ACCESS_TOKEN ||
    "pk.eyJ1Ijoic2VtaWxvY2F0b3IyMDI1IiwiYSI6ImNtanFteTA2eTMyMzIzaG9sODFzbjM4NWgifQ.ulLE-vR8_Q_iNULAK71dbg" ||
    (
      window.DriveIQAdventureConfig &&
      window.DriveIQAdventureConfig.mapbox &&
      window.DriveIQAdventureConfig.mapbox.accessToken
    ) ||
    (
      window.DriveIQConfig &&
      window.DriveIQConfig.mapboxToken
    ) ||
    ""
  );
}

function discoveryQuery(type){
  var key=clean(type||"around-me");

  if(key==="boat-ramp"){return "boat ramp";}
  if(key==="marina"){return "marina";}
  if(key==="fuel"){return "marine fuel dock";}
  if(key==="restaurant"){return "waterfront restaurant";}
  if(key==="fishing"){return "fishing access";}
  if(key==="beach"){return "beach cove";}
  if(key==="hangout"){return "sandbar party cove boating hangout raft up";}
  return "boat ramp marina marine fuel waterfront restaurant fishing access beach cove sandbar boating hangout";
}

function discoveryIcon(type){
  var key=clean(type).toLowerCase();
  if(key.indexOf("ramp")>=0){return "↘";}
  if(key.indexOf("marina")>=0){return "⚓";}
  if(key.indexOf("fuel")>=0){return "⛽";}
  if(key.indexOf("restaurant")>=0){return "🍽";}
  if(key.indexOf("fishing")>=0){return "🎣";}
  if(key.indexOf("beach")>=0||key.indexOf("cove")>=0){return "🌊";}
  if(key.indexOf("hangout")>=0||key.indexOf("sandbar")>=0||key.indexOf("party")>=0){return "☀";}
  return "◆";
}

function clearDiscoveryMarkers(){
  discoveryMarkers.forEach(function(entry){
    try{entry.marker.remove();}catch(error){}
  });

  discoveryMarkers=[];
  state.discoveryResults=[];
  selectedDiscoveryPlace=null;
  hide(byId("bdPlaceCard"));
  hide(byId("bdDiscoveryStatus"));

  Array.prototype.forEach.call(
    document.querySelectorAll("[data-bd-nearby]"),
    function(button){
      button.setAttribute("data-active","false");
    }
  );

  activeDiscoveryType="";
  return true;
}

function normalizeDiscoveryPlace(feature,type){
  var center=
    Array.isArray(feature&&feature.center)
      ? feature.center
      : feature&&feature.geometry&&
        Array.isArray(feature.geometry.coordinates)
        ? feature.geometry.coordinates
        : null;

  return {
    id:clean(feature&&feature.id||""),
    name:clean(
      feature&&(
        feature.text||
        feature.name||
        feature.place_name
      )||"Marine place"
    ),
    address:clean(
      feature&&feature.place_name||
      ""
    ),
    type:clean(type||"marine-place"),
    lng:center?number(center[0]):null,
    lat:center?number(center[1]):null
  };
}

function showDiscoveryStatus(message){
  setText("bdDiscoveryStatus",message);
  show(byId("bdDiscoveryStatus"));
}

function hideDiscoveryStatusLater(){
  window.clearTimeout(hideDiscoveryStatusLater.__timer);
  hideDiscoveryStatusLater.__timer=
    window.setTimeout(function(){
      hide(byId("bdDiscoveryStatus"));
    },1800);
}

function showPlaceCard(place){
  if(!place){return false;}

  selectedDiscoveryPlace=place;

  var metrics={
    miles:milesFromMeters(
      distanceMeters(state.location,place)
    ),
    bearing:bearingDegrees(state.location,place)
  };

  setText("bdPlaceName",place.name||"Marine place");
  setText("bdPlaceAddress",place.address||"Nearby marine destination");
  setText("bdPlaceDistance",formatDistance(metrics.miles));
  setText("bdPlaceBearing",headingText(metrics.bearing));
  setText(
    "bdPlaceType",
    clean(place.type||"Marine place")
      .replace(/[-_]/g," ")
  );

  show(byId("bdPlaceCard"));
  return true;
}

function fitDiscoveryResults(items){
  if(!map||!items||!items.length){return false;}

  var valid=items.filter(validCoordinate);
  if(!valid.length){return false;}

  var bounds=new mapboxgl.LngLatBounds();
  var origin=currentOriginCoordinate();

  if(origin){bounds.extend(origin);}

  valid.forEach(function(place){
    bounds.extend(coordinate(place));
  });

  map.fitBounds(bounds,{
    padding:{
      top:170,
      right:55,
      bottom:300,
      left:55
    },
    maxZoom:14.5,
    duration:700
  });

  return true;
}

function paintDiscoveryMarkers(items,type){
  clearDiscoveryMarkers();

  activeDiscoveryType=clean(type||"around-me");

  Array.prototype.forEach.call(
    document.querySelectorAll("[data-bd-nearby]"),
    function(button){
      button.setAttribute(
        "data-active",
        button.getAttribute("data-bd-nearby")===activeDiscoveryType
          ? "true"
          : "false"
      );
    }
  );

  (items||[])
    .filter(validCoordinate)
    .forEach(function(place){
      var element=createMarkerElement(
        "bd-discovery-marker",
        discoveryIcon(place.type),
        place.type||type
      );

      var marker=new mapboxgl.Marker({
        element:element,
        anchor:"bottom"
      })
        .setLngLat(coordinate(place))
        .addTo(map);

      element.addEventListener("click",function(){
        showPlaceCard(place);
      });

      discoveryMarkers.push({
        marker:marker,
        place:place
      });
    });

  state.discoveryResults=(items||[]).slice();
  fitDiscoveryResults(items);
  return true;
}

function searchDiscovery(type){
  var origin=locationFrom(
    state.location||
    state.trip&&state.trip.location
  );

  if(!origin){
    showDiscoveryStatus("Waiting for your location…");
    return Promise.resolve([]);
  }

  var key=clean(type||"around-me");

  if(activeDiscoveryType===key&&discoveryMarkers.length){
    clearDiscoveryMarkers();
    return Promise.resolve([]);
  }

  if(discoveryAbortController){
    try{discoveryAbortController.abort();}catch(error){}
  }

  discoveryAbortController=
    typeof AbortController!=="undefined"
      ? new AbortController()
      : null;

  showDiscoveryStatus(
    key==="around-me"
      ? "Scanning useful places around this water…"
      : "Scanning nearby "+key.replace(/-/g," ")+"…"
  );

  var query=discoveryQuery(key);
  var url=
    "https://api.mapbox.com/geocoding/v5/mapbox.places/"+
    encodeURIComponent(query)+
    ".json?access_token="+
    encodeURIComponent(mapboxToken())+
    "&autocomplete=false"+
    "&limit=10"+
    "&types=poi"+
    "&proximity="+
    encodeURIComponent(origin.lng+","+origin.lat);

  return fetch(url,{
    method:"GET",
    credentials:"omit",
    signal:discoveryAbortController
      ? discoveryAbortController.signal
      : undefined
  })
    .then(function(response){
      if(!response.ok){
        throw new Error("Marine discovery failed");
      }
      return response.json();
    })
    .then(function(payload){
      var items=Array.isArray(payload&&payload.features)
        ? payload.features.map(function(feature){
            return normalizeDiscoveryPlace(feature,key);
          })
        : [];

      paintDiscoveryMarkers(items,key);

      showDiscoveryStatus(
        items.length
          ? items.length+" places nearby"
          : "No trusted places found nearby"
      );

      hideDiscoveryStatusLater();

      emit("boating:marine-discovery-completed",{
        type:key,
        count:items.length,
        results:items
      });

      return items;
    })
    .catch(function(error){
      if(error&&error.name==="AbortError"){
        return [];
      }

      showDiscoveryStatus("Marine discovery could not complete");
      hideDiscoveryStatusLater();
      return [];
    });
}

function mapStyle(){
  return clean(
    window.DRIVE_IQ_BOATING_MAP_STYLE ||
    (
      window.DriveIQAdventureConfig &&
      window.DriveIQAdventureConfig.mapbox &&
      window.DriveIQAdventureConfig.mapbox.style
    ) ||
    "mapbox://styles/mapbox/navigation-night-v1"
  );
}

function locationFrom(value){
  if(!value||typeof value!=="object"){return null;}

  var lat=number(value.lat!=null?value.lat:value.latitude);
  var lng=number(value.lng!=null?value.lng:value.longitude);

  if(lat===null||lng===null||Math.abs(lat)>90||Math.abs(lng)>180){
    return null;
  }

  return {
    lat:lat,
    lng:lng,
    accuracyMeters:number(value.accuracyMeters!=null?value.accuracyMeters:value.accuracy),
    speedMps:number(value.speedMps!=null?value.speedMps:value.speed),
    heading:number(value.heading),
    capturedAt:Number(value.capturedAt||value.timestamp||Date.now())
  };
}

function validCoordinate(value){
  return !!locationFrom(value);
}

function coordinate(value){
  var point=locationFrom(value);
  return point?[point.lng,point.lat]:null;
}

function destination(){return state.trip&&state.trip.destination||null;}
function savedLaunch(){
  return (
    state.homeBase||
    state.trip&&(
      state.trip.savedLaunch||
      state.trip.launch
    )||
    null
  );
}
function launch(){return savedLaunch();}

function decodeTripFromQuery(){
  var params,encoded;
  try{
    params=new URLSearchParams(window.location.search);
    encoded=params.get("trip");
  }catch(error){encoded="";}

  if(!encoded){return null;}

  try{
    return JSON.parse(
      decodeURIComponent(
        escape(
          window.atob(
            decodeURIComponent(encoded)
          )
        )
      )
    );
  }catch(error2){
    return null;
  }
}

function restoreTrip(){
  var trip=decodeTripFromQuery();

  if(!trip){
    try{
      trip=JSON.parse(window.sessionStorage.getItem(TRIP_STORAGE_KEY)||"null");
    }catch(error){trip=null;}
  }

  state.trip=trip&&typeof trip==="object"?trip:null;
  return state.trip;
}

function saveState(){
  try{
    window.localStorage.setItem(
      DRIVE_STATE_KEY,
      JSON.stringify({
        build:BUILD,
        trip:state.trip,
        route:state.route,
        guidanceActive:state.guidanceActive,
        awarenessActive:state.awarenessActive,
        returnMode:state.returnMode,
        breadcrumb:state.breadcrumb,
        homeBase:state.homeBase,
        launchDetection:state.launchDetection,
        selectedIntel:state.selectedIntel,
        cardState:state.cardState,
        updatedAt:Date.now()
      })
    );
  }catch(error){}
}


function toast(message){
  var node=byId("bdBoatingToast");

  if(!node){
    node=document.createElement("div");
    node.id="bdBoatingToast";
    node.setAttribute("role","status");
    node.style.cssText=[
      "position:fixed",
      "left:50%",
      "bottom:110px",
      "transform:translateX(-50%)",
      "z-index:99999",
      "max-width:min(88vw,420px)",
      "padding:11px 14px",
      "border-radius:999px",
      "background:rgba(4,15,24,.97)",
      "border:1px solid rgba(255,228,130,.26)",
      "color:#fff",
      "box-shadow:0 16px 40px rgba(0,0,0,.35)",
      "font:800 11px/1.25 system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      "text-align:center",
      "opacity:0",
      "transition:opacity .2s ease"
    ].join(";");
    document.body.appendChild(node);
  }

  node.textContent=clean(message);
  node.style.opacity="1";

  window.clearTimeout(node.__bdTimer);
  node.__bdTimer=window.setTimeout(function(){
    node.style.opacity="0";
  },2200);
}

function ensureTrip(){
  if(!state.trip||typeof state.trip!=="object"){
    state.trip={};
  }
  return state.trip;
}

function homeBaseFromLocation(name){
  var location=locationFrom(state.location);

  if(!location){
    return null;
  }

  return {
    id:"home-base-"+Date.now(),
    name:clean(name||"My Launch / Parked Here"),
    type:"saved_launch",
    source:"drive-iq-home-base",
    lat:location.lat,
    lng:location.lng,
    accuracyMeters:location.accuracyMeters,
    capturedAt:Date.now()
  };
}

function persistHomeBase(){
  var trip=ensureTrip();

  trip.savedLaunch=state.homeBase;
  trip.launch=state.homeBase;

  try{
    window.sessionStorage.setItem(
      TRIP_STORAGE_KEY,
      JSON.stringify(trip)
    );
  }catch(error){}

  saveState();
  return true;
}

function markHomeBase(options){
  var supplied=options||{};
  var home=homeBaseFromLocation(
    supplied.name||
    "My Launch / Parked Here"
  );

  if(!home){
    toast("Waiting for a reliable GPS position");
    return false;
  }

  state.homeBase=home;
  state.returnMode=false;
  state.launchDetection.prompted=false;
  state.launchDetection.dismissed=false;
  launchPromptCandidate=null;

  persistHomeBase();
  hide(byId("bdLaunchPrompt"));
  render();
  updateMarkers();
  updateRouteLayers();
  fitActiveTrip();
  refreshIntelligence();

  toast("Home Base saved");

  emit("boating:home-base-saved",{
    homeBase:state.homeBase,
    source:clean(supplied.source||"manual")
  });

  return state.homeBase;
}

function replaceHomeBase(){
  if(
    savedLaunch()&&
    !window.confirm(
      "Replace your current Home Base with this location?"
    )
  ){
    return false;
  }

  return markHomeBase({
    source:"replace"
  });
}

function renameHomeBase(){
  var home=savedLaunch();

  if(!home){
    return markHomeBase({
      source:"rename-empty"
    });
  }

  var nextName=window.prompt(
    "Name this Home Base",
    home.name||"My Launch / Parked Here"
  );

  if(nextName===null){
    return false;
  }

  nextName=clean(nextName);

  if(!nextName){
    toast("Home Base name was not changed");
    return false;
  }

  state.homeBase=Object.assign({},home,{
    name:nextName
  });

  persistHomeBase();
  render();
  updateMarkers();
  refreshIntelligence();
  toast("Home Base renamed");

  return state.homeBase;
}

function moveHomeBase(){
  return replaceHomeBase();
}

function clearHomeBase(){
  if(
    savedLaunch()&&
    !window.confirm(
      "Clear your saved Home Base for this session?"
    )
  ){
    return false;
  }

  state.homeBase=null;
  state.returnMode=false;

  if(state.trip){
    state.trip.savedLaunch=null;
    state.trip.launch=null;
  }

  try{
    window.sessionStorage.setItem(
      TRIP_STORAGE_KEY,
      JSON.stringify(state.trip||{})
    );
  }catch(error){}

  if(launchMarker){
    try{launchMarker.remove();}catch(error){}
    launchMarker=null;
  }

  saveState();
  render();
  updateRouteLayers();
  refreshIntelligence();
  toast("Home Base cleared");

  emit("boating:home-base-cleared",{});
  return true;
}

function renderHomeBaseControls(){
  var home=savedLaunch();
  var manager=byId("bdHomeManager");

  if(home){
    show(manager);
    setText(
      "bdHomeManagerName",
      home.name||"My Launch / Parked Here"
    );
    setText(
      "bdHomeManagerCopy",
      formatDistance(returnMetrics().miles)+
      " away • Home Base is saved for this session."
    );

    var mapButton=byId("bdDropHomePinMap");
    if(mapButton){
      mapButton.textContent="📍 HOME BASE SAVED";
    }
  }else{
    hide(manager);

    var emptyMapButton=byId("bdDropHomePinMap");
    if(emptyMapButton){
      emptyMapButton.textContent="📍 DROP HOME PIN";
    }
  }
}

function launchDetectionContext(){
  return {
    location:state.location,
    speedMph:state.speedMph,
    heading:state.heading,
    breadcrumb:state.breadcrumb.slice(-20),
    discoveryResults:state.discoveryResults.slice(),
    homeBase:savedLaunch(),
    trip:state.trip
  };
}

function nearestRampDistanceMeters(){
  var nearest=null;

  (state.discoveryResults||[]).forEach(function(place){
    var type=clean(place&&place.type).toLowerCase();

    if(
      type.indexOf("ramp")<0&&
      type.indexOf("marina")<0&&
      type.indexOf("dock")<0&&
      type.indexOf("launch")<0
    ){
      return;
    }

    var distance=distanceMeters(
      state.location,
      place
    );

    if(
      distance!==null&&
      (nearest===null||distance<nearest)
    ){
      nearest=distance;
    }
  });

  return nearest;
}

function recentMovementMeters(){
  var samples=launchDetectionSamples;

  if(samples.length<2){
    return 0;
  }

  return distanceMeters(
    samples[0],
    samples[samples.length-1]
  )||0;
}

function launchDetectionScore(){
  if(
    savedLaunch()||
    !state.location
  ){
    return {
      score:0,
      reason:""
    };
  }

  var score=0;
  var reasons=[];
  var speed=number(state.speedMph);
  var accuracy=number(state.location.accuracyMeters);
  var rampDistance=nearestRampDistanceMeters();
  var moved=recentMovementMeters();

  if(accuracy!==null&&accuracy<=35){
    score+=1;
  }

  if(speed!==null&&speed<=4){
    score+=2;
    reasons.push("low-speed movement");
  }

  if(speed!==null&&speed<=1){
    score+=1;
  }

  if(rampDistance!==null&&rampDistance<=180){
    score+=4;
    reasons.push("near a ramp or marina");
  }else if(rampDistance!==null&&rampDistance<=400){
    score+=2;
    reasons.push("approaching a launch area");
  }

  if(
    launchDetectionSamples.length>=4&&
    moved>=20&&
    moved<=220
  ){
    score+=1;
  }

  if(
    state.breadcrumb.length>=3&&
    state.speedMph>=3
  ){
    score+=1;
  }

  return {
    score:score,
    reason:reasons.join(" and ")||
      "launch-like movement near the water"
  };
}

function showLaunchPrompt(candidate){
  if(!candidate||candidate.score<5){
    return false;
  }

  launchPromptCandidate={
    location:locationFrom(state.location),
    score:candidate.score,
    reason:candidate.reason,
    capturedAt:Date.now()
  };

  state.launchDetection.prompted=true;
  state.launchDetection.confidence=candidate.score;
  state.launchDetection.reason=candidate.reason;
  launchDetectionLastPromptAt=Date.now();

  setText(
    "bdLaunchPromptTitle",
    candidate.score>=7
      ? "Are you launching here?"
      : "Save this as Home Base?"
  );

  setText(
    "bdLaunchPromptCopy",
    "Drive IQ detected "+
    candidate.reason+
    ". Save this location so you can always get back to your car, truck, ramp, dock, or marina."
  );

  show(byId("bdLaunchPrompt"));
  saveState();

  emit("boating:launch-detection-prompted",{
    candidate:launchPromptCandidate
  });

  return true;
}

function dismissLaunchPrompt(){
  hide(byId("bdLaunchPrompt"));
  launchDetectionDismissedAt=Date.now();
  state.launchDetection.dismissed=true;
  state.launchDetection.prompted=false;
  saveState();

  emit("boating:launch-detection-dismissed",{});
  return true;
}

function confirmLaunchPrompt(){
  if(
    launchPromptCandidate&&
    launchPromptCandidate.location
  ){
    state.location=locationFrom(
      launchPromptCandidate.location
    );
  }

  return markHomeBase({
    source:"launch-detection"
  });
}

function evaluateLaunchDetection(){
  if(
    savedLaunch()||
    !state.location
  ){
    return false;
  }

  launchDetectionSamples.push({
    lat:state.location.lat,
    lng:state.location.lng,
    speedMph:state.speedMph,
    capturedAt:Date.now()
  });

  if(launchDetectionSamples.length>12){
    launchDetectionSamples=
      launchDetectionSamples.slice(-12);
  }

  if(
    Date.now()-launchDetectionDismissedAt<
    15*60*1000
  ){
    return false;
  }

  if(
    Date.now()-launchDetectionLastPromptAt<
    5*60*1000
  ){
    return false;
  }

  var candidate=launchDetectionScore();

  if(candidate.score>=5){
    return showLaunchPrompt(candidate);
  }

  return false;
}

function radians(value){return Number(value)*Math.PI/180;}

function distanceMeters(a,b){
  var first=locationFrom(a);
  var second=locationFrom(b);
  var radius=6371000;
  var dLat,dLng,h;

  if(!first||!second){return null;}

  dLat=radians(second.lat-first.lat);
  dLng=radians(second.lng-first.lng);
  h=
    Math.sin(dLat/2)*Math.sin(dLat/2)+
    Math.cos(radians(first.lat))*
    Math.cos(radians(second.lat))*
    Math.sin(dLng/2)*
    Math.sin(dLng/2);

  return radius*2*Math.atan2(Math.sqrt(h),Math.sqrt(1-h));
}

function milesFromMeters(value){
  var meters=number(value);
  return meters===null?null:meters/1609.344;
}

function formatDistance(miles){
  var value=number(miles);
  if(value===null){return "-- mi";}
  if(value<0.1){return Math.max(50,Math.round(value*5280/50)*50)+" ft";}
  if(value<10){return value.toFixed(1)+" mi";}
  return Math.round(value)+" mi";
}

function formatEta(minutes){
  var value=number(minutes);
  if(value===null){return "--";}
  if(value<60){return Math.max(1,Math.round(value))+" min";}
  return Math.floor(value/60)+"h "+Math.round(value%60)+"m";
}

function headingText(value){
  var heading=number(value);
  if(heading===null){return "--°";}
  return Math.round(((heading%360)+360)%360)+"°";
}

function bearingDegrees(a,b){
  var first=locationFrom(a);
  var second=locationFrom(b);
  if(!first||!second){return null;}

  var lat1=radians(first.lat);
  var lat2=radians(second.lat);
  var dLng=radians(second.lng-first.lng);
  var y=Math.sin(dLng)*Math.cos(lat2);
  var x=
    Math.cos(lat1)*Math.sin(lat2)-
    Math.sin(lat1)*Math.cos(lat2)*Math.cos(dLng);

  return (Math.atan2(y,x)*180/Math.PI+360)%360;
}

function averageReturnSpeedMph(){
  var speed=number(state.speedMph);
  if(speed!==null&&speed>=2){
    return Math.max(3,Math.min(45,speed));
  }

  var profile=clean(
    state.trip&&state.trip.boatProfile||"pontoon"
  ).toLowerCase();

  if(profile==="pwc"){return 24;}
  if(profile==="fishing"){return 20;}
  return 16;
}

function returnMetrics(){
  var home=savedLaunch();
  var current=state.location||
    state.trip&&state.trip.location;

  var meters=distanceMeters(current,home);
  var miles=milesFromMeters(meters);
  var bearing=bearingDegrees(current,home);
  var speed=averageReturnSpeedMph();
  var minutes=
    miles!==null&&speed>0
      ? miles/speed*60
      : null;

  return {
    miles:miles,
    bearing:bearing,
    minutes:minutes
  };
}

function addBreadcrumbPoint(location){
  var point=locationFrom(location);
  if(!point){return false;}

  var previous=state.breadcrumb.length
    ? state.breadcrumb[state.breadcrumb.length-1]
    : null;

  if(previous){
    var moved=distanceMeters(previous,point);
    if(moved!==null&&moved<12){
      return false;
    }
  }

  state.breadcrumb.push({
    lat:point.lat,
    lng:point.lng,
    capturedAt:point.capturedAt
  });

  if(state.breadcrumb.length>2500){
    state.breadcrumb=state.breadcrumb.slice(-2000);
  }

  return true;
}

function emptyFeatureCollection(){
  return {type:"FeatureCollection",features:[]};
}

function lineFeature(coordinates,properties){
  return {
    type:"Feature",
    properties:properties||{},
    geometry:{
      type:"LineString",
      coordinates:coordinates
    }
  };
}

function normalizedGeometry(value){
  var geometry=value;

  if(value&&value.type==="Feature"){
    geometry=value.geometry;
  }

  if(value&&value.type==="LineString"){
    geometry=value.coordinates;
  }

  if(value&&value.geometry&&value.geometry.type==="LineString"){
    geometry=value.geometry.coordinates;
  }

  if(!Array.isArray(geometry)){return [];}

  return geometry
    .map(function(point){
      if(Array.isArray(point)&&point.length>=2){
        var lng=number(point[0]);
        var lat=number(point[1]);
        if(lng!==null&&lat!==null&&Math.abs(lat)<=90&&Math.abs(lng)<=180){
          return [lng,lat];
        }
      }

      var normalized=coordinate(point);
      return normalized;
    })
    .filter(Boolean);
}

function routeGeometry(){
  return normalizedGeometry(
    state.route&&(
      state.route.geometry||
      state.route.routeGeometry||
      state.route.geojson
    )
  );
}

function createMarkerElement(className,text,kind){
  var element=document.createElement("div");
  element.className=className;

  if(kind){
    element.setAttribute(
      "data-kind",
      clean(kind).toLowerCase()
    );
  }

  if(className==="bd-marker"){
    var cabin=document.createElement("span");
    var wake=document.createElement("span");

    cabin.className="bd-boat-cabin";
    wake.className="bd-boat-wake";

    element.appendChild(cabin);
    element.appendChild(wake);
    element.setAttribute("aria-label","Current vessel position");
    return element;
  }

  if(
    className==="bd-marker-launch"||
    className==="bd-marker-destination"
  ){
    var icon=document.createElement("span");
    icon.textContent=text||"";
    element.appendChild(icon);
    return element;
  }

  if(text){
    element.textContent=text;
  }

  return element;
}

function setMapStatus(message,visible){
  setText("bdMapStatus",message);
  var node=byId("bdMapStatus");
  if(node){
    if(visible===false){hide(node);}
    else{show(node);}
  }
}

function ensureSourcesAndLayers(){
  if(!map||!map.isStyleLoaded()){return false;}

  if(!map.getSource(ROUTE_SOURCE_ID)){
    map.addSource(ROUTE_SOURCE_ID,{
      type:"geojson",
      data:emptyFeatureCollection()
    });
  }

  if(!map.getLayer(ROUTE_CASING_LAYER_ID)){
    map.addLayer({
      id:ROUTE_CASING_LAYER_ID,
      type:"line",
      source:ROUTE_SOURCE_ID,
      layout:{
        "line-cap":"round",
        "line-join":"round"
      },
      paint:{
        "line-color":"rgba(2,10,16,.88)",
        "line-width":10,
        "line-opacity":0.9
      }
    });
  }

  if(!map.getLayer(ROUTE_LAYER_ID)){
    map.addLayer({
      id:ROUTE_LAYER_ID,
      type:"line",
      source:ROUTE_SOURCE_ID,
      layout:{
        "line-cap":"round",
        "line-join":"round"
      },
      paint:{
        "line-color":"#31e5cf",
        "line-width":6,
        "line-opacity":0.96,
        "line-blur":0.4
      }
    });
  }

  if(!map.getSource(BEARING_SOURCE_ID)){
    map.addSource(BEARING_SOURCE_ID,{
      type:"geojson",
      data:emptyFeatureCollection()
    });
  }

  if(!map.getLayer(BEARING_LAYER_ID)){
    map.addLayer({
      id:BEARING_LAYER_ID,
      type:"line",
      source:BEARING_SOURCE_ID,
      layout:{
        "line-cap":"round",
        "line-join":"round"
      },
      paint:{
        "line-color":"#ffe482",
        "line-width":3,
        "line-opacity":0.78,
        "line-dasharray":[1.4,1.4]
      }
    });
  }

  if(!map.getSource(BREADCRUMB_SOURCE_ID)){
    map.addSource(BREADCRUMB_SOURCE_ID,{
      type:"geojson",
      data:emptyFeatureCollection()
    });
  }

  if(!map.getLayer(BREADCRUMB_LAYER_ID)){
    map.addLayer({
      id:BREADCRUMB_LAYER_ID,
      type:"line",
      source:BREADCRUMB_SOURCE_ID,
      layout:{
        "line-cap":"round",
        "line-join":"round"
      },
      paint:{
        "line-color":"#6df0ca",
        "line-width":3,
        "line-opacity":0.58
      }
    });
  }

  if(!map.getSource(RETURN_SOURCE_ID)){
    map.addSource(RETURN_SOURCE_ID,{
      type:"geojson",
      data:emptyFeatureCollection()
    });
  }

  if(!map.getLayer(RETURN_LAYER_ID)){
    map.addLayer({
      id:RETURN_LAYER_ID,
      type:"line",
      source:RETURN_SOURCE_ID,
      layout:{
        "line-cap":"round",
        "line-join":"round"
      },
      paint:{
        "line-color":"#ffe482",
        "line-width":4,
        "line-opacity":0.92,
        "line-dasharray":[1.2,1.1]
      }
    });
  }

  return true;
}

function setGeoJSON(sourceId,data){
  if(!map||!map.isStyleLoaded()){return false;}
  var source=map.getSource(sourceId);
  if(!source||typeof source.setData!=="function"){return false;}
  source.setData(data);
  return true;
}

function currentOriginCoordinate(){
  return (
    coordinate(state.location) ||
    coordinate(launch()) ||
    coordinate(state.trip&&state.trip.location)
  );
}

function destinationCoordinate(){
  return coordinate(destination());
}

function connectedRouteCoordinates(){
  var geometry=routeGeometry();
  var origin=currentOriginCoordinate();

  if(geometry.length<2){return [];}

  if(origin){
    var first=geometry[0];
    var distance=milesFromMeters(
      distanceMeters(
        {lng:origin[0],lat:origin[1]},
        {lng:first[0],lat:first[1]}
      )
    );

    if(distance!==null&&distance<2){
      geometry[0]=origin;
    }else{
      geometry.unshift(origin);
    }
  }

  return geometry;
}

function updateRouteLayers(){
  if(!ensureSourcesAndLayers()){return false;}

  var route=connectedRouteCoordinates();
  var origin=currentOriginCoordinate();
  var end=destinationCoordinate();
  var home=coordinate(savedLaunch());

  if(route.length>=2){
    setGeoJSON(ROUTE_SOURCE_ID,{
      type:"FeatureCollection",
      features:[lineFeature(route,{kind:"marine-route"})]
    });
  }else{
    setGeoJSON(ROUTE_SOURCE_ID,emptyFeatureCollection());
  }

  if(origin&&end&&!state.returnMode&&route.length<2){
    setGeoJSON(BEARING_SOURCE_ID,{
      type:"FeatureCollection",
      features:[lineFeature([origin,end],{kind:"bearing-only"})]
    });
  }else{
    setGeoJSON(BEARING_SOURCE_ID,emptyFeatureCollection());
  }

  var breadcrumbCoords=(state.breadcrumb||[])
    .map(coordinate)
    .filter(Boolean);

  if(breadcrumbCoords.length>=2){
    setGeoJSON(BREADCRUMB_SOURCE_ID,{
      type:"FeatureCollection",
      features:[lineFeature(
        breadcrumbCoords,
        {kind:"breadcrumb"}
      )]
    });
  }else{
    setGeoJSON(BREADCRUMB_SOURCE_ID,emptyFeatureCollection());
  }

  if(origin&&home&&state.returnMode){
    setGeoJSON(RETURN_SOURCE_ID,{
      type:"FeatureCollection",
      features:[lineFeature(
        [origin,home],
        {kind:"return-home"}
      )]
    });
  }else{
    setGeoJSON(RETURN_SOURCE_ID,emptyFeatureCollection());
  }

  return true;
}

function updateMarkers(){
  if(!map){return false;}

  var vessel=currentOriginCoordinate();
  var launchPoint=coordinate(savedLaunch());
  var end=destinationCoordinate();

  if(vessel){
    if(!vesselMarker){
      vesselMarker=new mapboxgl.Marker({
        element:createMarkerElement("bd-marker","","vessel"),
        anchor:"center",
        rotationAlignment:"map",
        pitchAlignment:"map"
      });
    }

    vesselMarker
      .setLngLat(vessel)
      .setRotation(
        number(state.heading)!==null
          ? Number(state.heading)
          : 0
      )
      .addTo(map);
  }

  if(launchPoint){
    if(!launchMarker){
      launchMarker=new mapboxgl.Marker({
        element:createMarkerElement("bd-marker-launch","⚓","home-base"),
        anchor:"bottom"
      });
    }
    launchMarker.setLngLat(launchPoint).addTo(map);
  }else if(launchMarker){
    launchMarker.remove();
    launchMarker=null;
  }

  if(end){
    if(!destinationMarker){
      destinationMarker=new mapboxgl.Marker({
        element:createMarkerElement("bd-marker-destination","≈","selected-water"),
        anchor:"bottom"
      });
    }
    destinationMarker.setLngLat(end).addTo(map);
  }

  return true;
}

function fitActiveTrip(){
  if(!map){return false;}

  var points=[];
  var route=connectedRouteCoordinates();

  if(route.length){
    points=points.concat(route);
  }else{
    var origin=currentOriginCoordinate();
    var end=destinationCoordinate();
    var home=coordinate(savedLaunch());
    if(origin){points.push(origin);}
    if(home){points.push(home);}
    if(end){points.push(end);}
  }

  if(!points.length){return false;}

  if(points.length===1){
    map.easeTo({
      center:points[0],
      zoom:13.5,
      pitch:46,
      duration:700
    });
    return true;
  }

  var bounds=new mapboxgl.LngLatBounds();
  points.forEach(function(point){bounds.extend(point);});

  map.fitBounds(bounds,{
    padding:{
      top:190,
      right:80,
      bottom:260,
      left:80
    },
    maxZoom:14.5,
    duration:850
  });

  return true;
}

function initializeMap(){
  var token=mapboxToken();

  if(!window.mapboxgl){
    setMapStatus("Mapbox failed to load",true);
    state.lastError="Mapbox GL JS is unavailable";
    return Promise.resolve(false);
  }

  if(!token){
    setMapStatus("Mapbox token not found",true);
    state.lastError="Drive IQ Mapbox token is not exposed to Boating Drive";
    return Promise.resolve(false);
  }

  mapboxgl.accessToken=token;

  var initialCenter=
    currentOriginCoordinate()||
    destinationCoordinate()||
    [-86.4986,30.3859];

  map=new mapboxgl.Map({
    container:"bdMap",
    style:mapStyle(),
    center:initialCenter,
    zoom:12.5,
    pitch:46,
    bearing:0,
    attributionControl:false,
    antialias:true
  });

  map.addControl(
    new mapboxgl.NavigationControl({
      showCompass:true,
      showZoom:false,
      visualizePitch:true
    }),
    "bottom-right"
  );

  return new Promise(function(resolve){
    map.once("load",function(){
      ensureSourcesAndLayers();
      updateMarkers();
      updateRouteLayers();
      fitActiveTrip();
      setMapStatus("",false);
      emit("boating:map-ready",{build:BUILD});
      resolve(true);
    });

    map.on("error",function(event){
      state.lastError=clean(
        event&&event.error&&event.error.message||
        "Mapbox map error"
      );
      emit("boating:map-error",{message:state.lastError});
    });
  });
}

function routeContext(){
  return {
    build:BUILD,
    trip:state.trip,
    route:state.route,
    location:state.location,
    guidanceActive:state.guidanceActive,
    awarenessActive:state.awarenessActive,
    returnMode:state.returnMode,
    savedLaunch:savedLaunch(),
    homeBase:savedLaunch(),
    launchDetection:state.launchDetection,
    breadcrumb:state.breadcrumb.slice(),
    boatProfile:state.trip&&state.trip.boatProfile||"pontoon"
  };
}

function normalizeRoute(value){
  var source=value&&typeof value==="object"?value:{};
  var maneuver=source.maneuver||source.nextManeuver||{};
  var geometry=normalizedGeometry(
    source.geometry||
    source.routeGeometry||
    source.geojson
  );

  return {
    id:clean(source.id||""),
    source:clean(source.source||"drive-iq-boating"),
    geometry:geometry,
    distanceMiles:number(source.distanceMiles!=null?source.distanceMiles:source.distance),
    etaMinutes:number(source.etaMinutes!=null?source.etaMinutes:source.durationMinutes),
    confidence:clean(source.confidence||source.routeConfidence||(geometry.length>=2?"Route Strong":"Route Pending")),
    waterStatus:clean(source.waterStatus||source.waterLevel||"Unknown"),
    maneuver:{
      icon:clean(maneuver.icon||source.maneuverIcon||"↑"),
      distanceText:clean(maneuver.distanceText||source.maneuverDistance||"--"),
      instruction:clean(
        maneuver.instruction||
        source.maneuverInstruction||
        (geometry.length>=2?"Continue on the marine route":"Waiting for a verified marine route")
      )
    },
    raw:source
  };
}

function applyRoute(route){
  state.route=normalizeRoute(route);
  state.distanceMiles=state.route.distanceMiles;
  state.etaText=formatEta(state.route.etaMinutes);
  state.routeConfidence=state.route.confidence;
  state.waterStatus=state.route.waterStatus;
  state.maneuver=state.route.maneuver;

  state.intelligence.route={
    title:"Route IQ",
    label:"Route IQ • "+state.routeConfidence.replace(/^Route\s+/i,""),
    state:/strong|ready|good/i.test(state.routeConfidence)?"good":/warning|low|caution/i.test(state.routeConfidence)?"warning":"",
    copy:
      state.route.geometry.length>=2
        ? "The active marine route is connected to the current vessel position."
        : "No verified marine route geometry has been returned. The dashed line is bearing-only and is not navigational guidance.",
    alerts:
      state.route.geometry.length>=2
        ? []
        : [{
            title:"Verified route still needed",
            message:"The dashed bearing line may cross land, shallow water, or restricted areas. Do not treat it as a safe boating route."
          }]
  };

  saveState();
  render();
  updateMarkers();
  updateRouteLayers();
  fitActiveTrip();

  emit("boating:route-ready",{route:state.route});
  return state.route;
}

function buildRoute(){
  return callHook(
    "DRIVE_IQ_BOATING_BUILD_ROUTE",
    routeContext()
  )
    .then(function(response){
      return applyRoute(response||{});
    })
    .catch(function(error){
      state.lastError=clean(error&&error.message||error);
      return applyRoute({});
    });
}

function refreshRoute(){
  if(!state.guidanceActive){return Promise.resolve(state.route);}

  return callHook(
    "DRIVE_IQ_BOATING_UPDATE_ROUTE",
    routeContext()
  )
    .then(function(response){
      if(response){applyRoute(response);}
      return state.route;
    })
    .catch(function(){return state.route;});
}

function intelligenceFromTrip(){
  var conditions=state.trip&&state.trip.conditions||{};
  var metrics=returnMetrics();
  var home=savedLaunch();

  state.intelligence.location={
    title:"Location IQ",
    label:state.location
      ? "Location IQ • Active"
      : "Location IQ • Checking",
    state:state.location?"good":"",
    copy:state.location
      ? "Your live vessel position is active and updating."
      : "Drive IQ is waiting for a reliable vessel position.",
    alerts:[]
  };

  state.intelligence.home={
    title:"Home Base",
    label:home
      ? "Home Base • "+formatDistance(metrics.miles)
      : "Home Base • Not Marked",
    state:home?"good":"warning",
    copy:home
      ? "Your saved launch remains highlighted for the entire session."
      : "Mark where you launched or parked before heading out.",
    alerts:home?[]:[{
      title:"Launch not marked",
      message:"Save your ramp, dock, harbor, or parked location so Drive IQ can guide you back."
    }]
  };

  state.intelligence.wind={
    title:"Wind IQ",
    label:conditions.windMph!=null
      ? "Wind IQ • "+Math.round(Number(conditions.windMph))+" mph"
      : "Wind IQ • Checking",
    state:conditions.windMph!=null&&Number(conditions.windMph)>=18
      ? "warning"
      : conditions.windMph!=null
        ? "good"
        : "",
    copy:clean(
      conditions.windNote||
      "Wind conditions are being monitored."
    ),
    alerts:conditions.windMph!=null&&Number(conditions.windMph)>=18
      ? [{
          title:"Wind increasing",
          message:"Conditions may become less comfortable or affect the return trip."
        }]
      : []
  };

  state.intelligence.weather={
    title:"Storm IQ",
    label:conditions.severe===true
      ? "Storm IQ • Watch"
      : conditions.summary
        ? "Storm IQ • "+clean(conditions.summary)
        : "Storm IQ • Monitoring",
    state:conditions.severe===true
      ? "warning"
      : conditions.summary
        ? "good"
        : "",
    copy:clean(
      conditions.weatherNote||
      conditions.summary||
      "Drive IQ is watching for meaningful weather changes."
    ),
    alerts:conditions.severe===true
      ? [{
          title:"Weather may affect your outing",
          message:"Review conditions and consider returning to your saved launch."
        }]
      : []
  };

  state.intelligence.water={
    title:"Water IQ",
    label:conditions.waterStatus
      ? "Water IQ • "+clean(conditions.waterStatus)
      : "Water IQ • Checking",
    state:conditions.waterWarning===true
      ? "warning"
      : conditions.waterStatus
        ? "good"
        : "",
    copy:clean(
      conditions.waterNote||
      "Water, tide, and level conditions are monitored where data is available."
    ),
    alerts:conditions.waterWarning===true
      ? [{
          title:"Water conditions changing",
          message:"Review the latest water information before continuing farther."
        }]
      : []
  };
}

function refreshIntelligence(){
  intelligenceFromTrip();

  return callHook(
    "DRIVE_IQ_BOATING_FETCH_INTELLIGENCE",
    routeContext()
  )
    .then(function(response){
      if(response&&typeof response==="object"){
        Object.keys(state.intelligence).forEach(function(key){
          if(response[key]){
            state.intelligence[key]=Object.assign({},state.intelligence[key],response[key]);
          }
        });

        if(response.routeConfidence){state.routeConfidence=clean(response.routeConfidence);}
        if(response.waterStatus){state.waterStatus=clean(response.waterStatus);}
      }

      render();
      return state.intelligence;
    })
    .catch(function(){render();return state.intelligence;});
}

function renderIntelChips(){
  Array.prototype.forEach.call(
    document.querySelectorAll("[data-bd-intel]"),
    function(button){
      var key=button.getAttribute("data-bd-intel");
      var data=state.intelligence[key];
      if(!data){return;}
      button.textContent=data.label;
      button.setAttribute("data-state",data.state||"");
    }
  );
}

function renderIntelPanel(){
  var data=state.intelligence[state.selectedIntel];
  if(!data){return;}

  setText("bdIntelPanelTitle",data.title);
  setText("bdIntelPanelCopy",data.copy);

  var alerts=byId("bdIntelAlerts");
  if(!alerts){return;}

  alerts.innerHTML="";
  var items=Array.isArray(data.alerts)?data.alerts:[];

  if(!items.length){
    items=[{
      title:"No immediate route change needed",
      message:"Drive IQ will surface this intelligence when it changes the boating decision."
    }];
  }

  items.slice(0,5).forEach(function(item){
    var node=document.createElement("div");
    node.className="bd-alert";
    node.innerHTML="<strong>"+clean(item.title||"Marine intelligence")+"</strong><span>"+clean(item.message||item.copy||"")+"</span>";
    alerts.appendChild(node);
  });
}

function render(){
  var home=savedLaunch();
  var metrics=returnMetrics();
  var conditions=state.trip&&state.trip.conditions||{};

  setText(
    "bdDestinationName",
    state.returnMode
      ? "Returning to your launch"
      : "You are on the water"
  );

  setText(
    "bdTripSummary",
    home
      ? "Home base saved • "+
        clean(state.trip&&state.trip.boatProfile||"pontoon")
      : "Mark your launch so the way back stays visible"
  );

  setText(
    "bdCurrentPositionSummary",
    state.location
      ? Number(state.location.lat).toFixed(5)+", "+
        Number(state.location.lng).toFixed(5)
      : "Resolving GPS…"
  );

  setText(
    "bdSavedLaunchSummary",
    home
      ? (home.name||"My Launch / Parked Here")+
        " • "+formatDistance(metrics.miles)
      : "Mark where you parked or launched."
  );

  setText(
    "bdConditionsSummary",
    conditions.summary||
    conditions.weatherNote||
    "Monitoring wind, storms, visibility, and water."
  );

  setText(
    "bdReturnSummary",
    home
      ? formatDistance(metrics.miles)+
        " away • Bearing "+
        headingText(metrics.bearing)
      : "Your launch marker will stay highlighted throughout the session."
  );

  setText(
    "bdSpeedValue",
    Math.max(0,Math.round(Number(state.speedMph||0)))
  );
  setText("bdHeadingValue",headingText(metrics.bearing));
  setText("bdEtaValue",formatEta(metrics.minutes));
  setText("bdDistanceValue",formatDistance(metrics.miles));
  setText("bdWaterStatus",state.waterStatus||"Checking");
  setText(
    "bdRouteConfidence",
    state.returnMode
      ? "Return Active"
      : state.awarenessActive
        ? "Awareness Active"
        : "Awareness Ready"
  );

  setText(
    "bdManeuverIcon",
    state.returnMode?"⌂":"◎"
  );
  setText(
    "bdManeuverDistance",
    state.returnMode
      ? formatDistance(metrics.miles)
      : state.location
        ? "Position active"
        : "Finding you…"
  );
  setText(
    "bdManeuverInstruction",
    state.returnMode
      ? "Bearing "+headingText(metrics.bearing)+
        " back to your saved launch"
      : "Live position, nearby places, and your saved launch stay visible here."
  );

  var awareness=byId("bdStartGuidance");
  if(awareness){
    awareness.textContent=
      state.awarenessActive
        ? "PAUSE AWARENESS"
        : "START AWARENESS";
  }

  renderHomeBaseControls();
  setCardState(state.cardState);
  renderIntelChips();
  renderIntelPanel();
}

function updateLocation(position){
  var coords=position&&position.coords?position.coords:position;
  var location=locationFrom({
    lat:coords&&coords.latitude,
    lng:coords&&coords.longitude,
    accuracy:coords&&coords.accuracy,
    speed:coords&&coords.speed,
    heading:coords&&coords.heading,
    capturedAt:position&&position.timestamp||Date.now()
  });

  if(!location){return false;}

  state.location=location;
  addBreadcrumbPoint(location);
  state.speedMph=location.speedMps!==null&&location.speedMps>=0?location.speedMps*2.236936:state.speedMph;
  if(location.heading!==null&&location.heading>=0){state.heading=location.heading;}

  updateMarkers();
  updateRouteLayers();
  render();
  evaluateLaunchDetection();

  emit("boating:location-updated",{location:state.location});
  return true;
}

function startLocationWatch(){
  if(!navigator.geolocation||watchId!==null){return false;}

  watchId=navigator.geolocation.watchPosition(
    updateLocation,
    function(error){
      state.lastError=clean(error&&error.message||"Location unavailable");
      emit("boating:location-failed",{message:state.lastError});
    },
    {
      enableHighAccuracy:true,
      timeout:10000,
      maximumAge:2000
    }
  );

  return true;
}

function stopLocationWatch(){
  if(watchId!==null&&navigator.geolocation){
    try{navigator.geolocation.clearWatch(watchId);}catch(error){}
  }
  watchId=null;
}

function setCardState(nextState){
  var card=byId("bdTripCard");
  var toggle=byId("bdCardToggle");
  var stateValue=
    nextState==="expanded"
      ? "expanded"
      : "compact";

  state.cardState=stateValue;

  if(card){
    card.setAttribute("data-state",stateValue);
  }

  if(toggle){
    toggle.textContent=
      stateValue==="expanded"
        ? "Collapse"
        : "Expand";
  }

  saveState();
  return stateValue;
}

function toggleCard(){
  return setCardState(
    state.cardState==="expanded"
      ? "compact"
      : "expanded"
  );
}

function centerMap(){
  if(!map){return false;}
  var origin=currentOriginCoordinate();
  if(origin){
    map.easeTo({
      center:origin,
      zoom:14,
      pitch:52,
      bearing:number(state.heading)||0,
      duration:650
    });
    return true;
  }
  return fitActiveTrip();
}

function openIntel(key){
  if(!state.intelligence[key]){return false;}
  state.selectedIntel=key;
  saveState();
  renderIntelPanel();
  show(byId("bdIntelPanel"));
  return true;
}

function toggleMenu(){
  var panel=byId("bdIntelPanel");
  if(!panel){return false;}
  if(panel.classList.contains("bd-hidden")){
    return openIntel(state.selectedIntel||"location");
  }
  hide(panel);
  return false;
}

function startAwareness(){
  state.awarenessActive=!state.awarenessActive;
  state.guidanceActive=state.awarenessActive;
  saveState();
  render();

  if(state.awarenessActive){
    refreshIntelligence();
    centerMap();
  }

  emit(
    state.awarenessActive
      ? "boating:awareness-started"
      : "boating:awareness-paused",
    routeContext()
  );

  return state.awarenessActive;
}

function returnToLaunch(){
  if(!savedLaunch()){
    state.selectedIntel="home";
    openIntel("home");
    setCardState("expanded");
    return false;
  }

  state.returnMode=true;
  state.awarenessActive=true;
  state.guidanceActive=true;
  saveState();
  updateRouteLayers();
  fitActiveTrip();
  render();

  emit("boating:return-to-launch",routeContext());
  return true;
}

function exploreNearby(type){
  var key=clean(type||"around-me");

  return callHook(
    "DRIVE_IQ_BOATING_EXPLORE_NEARBY",
    Object.assign({},routeContext(),{
      type:key
    })
  )
    .then(function(response){
      var hookResults=
        Array.isArray(response)
          ? response
          : response&&Array.isArray(response.results)
            ? response.results
            : [];

      if(hookResults.length){
        var normalized=hookResults.map(function(item){
          return {
            id:clean(item.id||""),
            name:clean(item.name||item.title||"Marine place"),
            address:clean(item.address||item.subtitle||""),
            type:clean(item.type||key),
            lat:number(item.lat!=null?item.lat:item.latitude),
            lng:number(item.lng!=null?item.lng:item.longitude)
          };
        }).filter(validCoordinate);

        paintDiscoveryMarkers(normalized,key);
        showDiscoveryStatus(normalized.length+" places nearby");
        hideDiscoveryStatusLater();
        return normalized;
      }

      return searchDiscovery(key);
    })
    .catch(function(){
      return searchDiscovery(key);
    });
}

function addStop(){
  setCardState("expanded");
  return exploreNearby("around-me");
}

function endTrip(){
  state.guidanceActive=false;
  state.awarenessActive=false;
  state.returnMode=false;
  clearDiscoveryMarkers();
  stopLocationWatch();

  if(intelligenceTimer){clearInterval(intelligenceTimer);}
  if(routeTimer){clearInterval(routeTimer);}

  return callHook("DRIVE_IQ_BOATING_END_TRIP",routeContext())
    .catch(function(){return null;})
    .then(function(){
      try{sessionStorage.removeItem(TRIP_STORAGE_KEY);}catch(error){}
      window.location.href=START_URL;
      return true;
    });
}

function bind(){
  if(byId("bdBackButton")){byId("bdBackButton").addEventListener("click",function(){window.location.href=START_URL;});}
  if(byId("bdMenuButton")){byId("bdMenuButton").addEventListener("click",toggleMenu);}
  if(byId("bdCenterMap")){byId("bdCenterMap").addEventListener("click",centerMap);}
  if(byId("bdAddStop")){byId("bdAddStop").addEventListener("click",addStop);}
  if(byId("bdEndTrip")){byId("bdEndTrip").addEventListener("click",endTrip);}
  if(byId("bdStartGuidance")){byId("bdStartGuidance").addEventListener("click",startAwareness);}
  if(byId("bdReturnToLaunch")){byId("bdReturnToLaunch").addEventListener("click",returnToLaunch);}
  if(byId("bdCardToggle")){byId("bdCardToggle").addEventListener("click",toggleCard);}
  if(byId("bdDropHomePinMap")){byId("bdDropHomePinMap").addEventListener("click",function(){markHomeBase({source:"map-button"});});}
  if(byId("bdMarkHomeBase")){byId("bdMarkHomeBase").addEventListener("click",function(){markHomeBase({source:"card-button"});});}
  if(byId("bdReplaceHomeBase")){byId("bdReplaceHomeBase").addEventListener("click",replaceHomeBase);}
  if(byId("bdRenameHomeBase")){byId("bdRenameHomeBase").addEventListener("click",renameHomeBase);}
  if(byId("bdMoveHomeBase")){byId("bdMoveHomeBase").addEventListener("click",moveHomeBase);}
  if(byId("bdClearHomeBase")){byId("bdClearHomeBase").addEventListener("click",clearHomeBase);}
  if(byId("bdConfirmLaunchPrompt")){byId("bdConfirmLaunchPrompt").addEventListener("click",confirmLaunchPrompt);}
  if(byId("bdDismissLaunchPrompt")){byId("bdDismissLaunchPrompt").addEventListener("click",dismissLaunchPrompt);}
  if(byId("bdClosePlaceCard")){
    byId("bdClosePlaceCard")
      .addEventListener("click",function(){
        hide(byId("bdPlaceCard"));
      });
  }

  if(byId("bdGoToPlace")){
    byId("bdGoToPlace")
      .addEventListener("click",function(){
        if(
          selectedDiscoveryPlace&&
          validCoordinate(selectedDiscoveryPlace)&&
          map
        ){
          map.easeTo({
            center:coordinate(selectedDiscoveryPlace),
            zoom:15,
            pitch:48,
            duration:650
          });
        }
      });
  }

  Array.prototype.forEach.call(
    document.querySelectorAll("[data-bd-nearby]"),
    function(button){
      button.addEventListener("click",function(){
        exploreNearby(
          button.getAttribute("data-bd-nearby")
        );
      });
    }
  );

  Array.prototype.forEach.call(
    document.querySelectorAll("[data-bd-intel]"),
    function(button){
      button.addEventListener("click",function(){
        openIntel(button.getAttribute("data-bd-intel"));
      });
    }
  );

  window.addEventListener("resize",function(){
    if(map){map.resize();}
  });
}

function init(){
  if(state.ready){return true;}

  restoreTrip();
  v34RestoreDriveState();
  v35ApplyWaterHandoff();
  bind();
  intelligenceFromTrip();

  state.location=locationFrom(
    state.trip&&state.trip.location
  );
  state.homeBase=locationFrom(
    state.trip&&(
      state.trip.savedLaunch||
      state.trip.launch
    )
  );

  if(state.homeBase){
    state.homeBase=Object.assign(
      {},
      state.trip.savedLaunch||
      state.trip.launch,
      state.homeBase
    );
  }
  state.awarenessActive=!!(
    state.trip&&state.trip.awarenessMode
  );
  state.guidanceActive=state.awarenessActive;
  state.returnMode=!!(
    state.trip&&state.trip.returnMode
  );
  state.breadcrumb=Array.isArray(
    state.trip&&state.trip.breadcrumb
  )
    ? state.trip.breadcrumb.slice()
    : [];

  if(state.location){
    addBreadcrumbPoint(state.location);
  }

  state.cardState="compact";
  state.ready=true;
  render();
  saveState();

  initializeMap()
    .then(function(){
      return callHook(
        "DRIVE_IQ_BOATING_DRIVE_INIT",
        routeContext()
      );
    })
    .then(function(response){
      if(response&&response.route){
        applyRoute(response.route);
      }
      v4RenderCommandCenter();
      return refreshIntelligence();
    })
    .catch(function(error){
      state.lastError=clean(error&&error.message||error);
      render();
    });

  startLocationWatch();

  intelligenceTimer=setInterval(refreshIntelligence,30000);
  routeTimer=setInterval(function(){
    if(state.awarenessActive&&state.route){refreshRoute();}
  },8000);

  emit("boating:drive-ready",{build:BUILD,trip:state.trip});
  return true;
}


var V34_SAVED_SPOTS_KEY="driveiq.boating.saved-spots.v1";
var V34_MANUAL_PINS_KEY="driveiq.boating.manual-pins.v1";
var v34SavedSpots=[];
var v34ManualPins=[];
var v34ManualMarkers=[];
var v34DropMode=false;
var v34SelectedSpot=null;

function v34Parse(value){
  try{return JSON.parse(String(value||""));}
  catch(error){return null;}
}

function v34Read(key){
  try{return v34Parse(localStorage.getItem(key));}
  catch(error){return null;}
}

function v34Write(key,value){
  try{
    localStorage.setItem(key,JSON.stringify(value));
    return true;
  }catch(error){
    return false;
  }
}

function v34Text(value){
  return String(value==null?"":value).replace(/\s+/g," ").trim();
}

function v34Num(value){
  var n=Number(value);
  return isFinite(n)?n:null;
}

function v34Id(prefix){
  return String(prefix||"spot")+"-"+Date.now()+"-"+Math.random().toString(36).slice(2,8);
}

function v34Escape(value){
  return v34Text(value).replace(/[&<>"']/g,function(ch){
    if(ch==="&"){return "&amp;";}
    if(ch==="<"){return "&lt;";}
    if(ch===">"){return "&gt;";}
    if(ch==='"'){return "&quot;";}
    return "&#39;";
  });
}

function v34PlaceKey(place){
  return [
    v34Text(place&&place.id),
    v34Text(place&&place.name).toLowerCase(),
    String(v34Num(place&&place.lat)),
    String(v34Num(place&&place.lng))
  ].join("|");
}

function v34Confidence(place){
  var source=v34Text(place&&place.source).toLowerCase();
  var type=v34Text(place&&place.type).toLowerCase();

  if(place&&place.verified===true){return "Verified";}
  if(place&&place.communityConfirmed===true){return "Community Confirmed";}
  if(source.indexOf("manual")>=0||type.indexOf("manual")>=0){return "Your Pin";}
  if(source.indexOf("hook")>=0||source.indexOf("drive-iq")>=0){return "Drive IQ Source";}
  return "Unverified";
}

function v34Vibes(place){
  var type=v34Text(place&&place.type).toLowerCase();
  var name=v34Text(place&&place.name).toLowerCase();
  var vibes=[];

  if(place&&Array.isArray(place.vibes)){
    vibes=place.vibes.slice(0,4).map(v34Text).filter(Boolean);
  }

  if(!vibes.length){
    if(type.indexOf("hangout")>=0||name.indexOf("sandbar")>=0||name.indexOf("party cove")>=0){
      vibes=["Local Hangout","Social","Worth Exploring"];
    }else if(type.indexOf("fishing")>=0){
      vibes=["Fishing","Local Knowledge","Outdoor"];
    }else if(type.indexOf("beach")>=0||type.indexOf("cove")>=0){
      vibes=["Scenic","Swimming","Relaxed"];
    }else if(type.indexOf("restaurant")>=0){
      vibes=["Waterfront Food","Dock-and-Dine","Social"];
    }else if(type.indexOf("marina")>=0){
      vibes=["Marina","Services","Access"];
    }else if(type.indexOf("fuel")>=0){
      vibes=["Fuel","Practical Stop"];
    }else if(type.indexOf("ramp")>=0){
      vibes=["Launch Access","Practical Stop"];
    }else{
      vibes=["Scenic","Worth Exploring"];
    }
  }

  return vibes.slice(0,4);
}

function v34Why(place){
  var type=v34Text(place&&place.type).toLowerCase();

  if(place&&place.why){return v34Text(place.why);}
  if(type.indexOf("hangout")>=0){
    return "A possible on-water social or local gathering spot. Confirm depth, access, conditions, and local restrictions before approaching.";
  }
  if(type.indexOf("fishing")>=0){
    return "A fishing-oriented place worth checking. Conditions, access, and productive areas can change, so use current local knowledge.";
  }
  if(type.indexOf("beach")>=0||type.indexOf("cove")>=0){
    return "A possible scenic or swimming stop. Check shoreline access, water depth, weather, and crowd conditions before committing.";
  }
  if(type.indexOf("restaurant")>=0){
    return "A possible waterfront food stop. Confirm dock access, operating hours, and whether transient boat parking is available.";
  }
  if(type.indexOf("marina")>=0||type.indexOf("fuel")>=0||type.indexOf("ramp")>=0){
    return "A practical marine stop. Confirm access, operating status, and local conditions before relying on it.";
  }
  return "A nearby place that may be worth exploring. Drive IQ will show stronger access and local-confidence signals as they become available.";
}

function v34NormalizePlace(place,options){
  var source=place&&typeof place==="object"?place:{};
  var supplied=options||{};
  var lat=v34Num(source.lat!=null?source.lat:source.latitude);
  var lng=v34Num(source.lng!=null?source.lng:source.longitude);

  return {
    id:v34Text(source.id||supplied.id||v34Id("marine-spot")),
    name:v34Text(source.name||source.title||supplied.name||"Saved Marine Spot"),
    address:v34Text(source.address||source.subtitle||supplied.address||""),
    type:v34Text(source.type||supplied.type||"marine-place"),
    source:v34Text(source.source||supplied.source||"drive-iq-boating"),
    lat:lat,
    lng:lng,
    verified:source.verified===true,
    communityConfirmed:source.communityConfirmed===true,
    vibes:Array.isArray(source.vibes)?source.vibes.slice(0,4):v34Vibes(source),
    why:v34Text(source.why||v34Why(source)),
    confidence:v34Text(source.confidence||v34Confidence(source)),
    savedAt:v34Num(source.savedAt)||null,
    createdAt:v34Num(source.createdAt)||Date.now()
  };
}

function v34LoadCollections(){
  var saved=v34Read(V34_SAVED_SPOTS_KEY);
  var pins=v34Read(V34_MANUAL_PINS_KEY);

  v34SavedSpots=Array.isArray(saved)
    ? saved.map(function(item){return v34NormalizePlace(item);}).filter(validCoordinate)
    : [];

  v34ManualPins=Array.isArray(pins)
    ? pins.map(function(item){return v34NormalizePlace(item,{type:"manual-pin",source:"manual-map-pin"});}).filter(validCoordinate)
    : [];
}

function v34PersistCollections(){
  v34Write(V34_SAVED_SPOTS_KEY,v34SavedSpots);
  v34Write(V34_MANUAL_PINS_KEY,v34ManualPins);
  v34RenderSavedSummary();
}

function v34IsSaved(place){
  var key=v34PlaceKey(place);
  var i;

  for(i=0;i<v34SavedSpots.length;i++){
    if(v34PlaceKey(v34SavedSpots[i])===key){return true;}
  }

  return false;
}

function v34RenderSavedSummary(){
  var count=v34SavedSpots.length;
  setText("bdSavedSpotsCount",count+" saved");
  setText(
    "bdSavedSpotsCopy",
    count
      ? "Your saved marine places persist across boating sessions."
      : "Save coves, fishing spots, hangouts, beaches, docks, and scenic places worth returning to."
  );
}

function v34RenderPlaceCard(place){
  if(!place){return false;}

  var normalized=v34NormalizePlace(place);
  v34SelectedSpot=normalized;
  selectedDiscoveryPlace=normalized;

  setText("bdPlaceConfidence",normalized.confidence||v34Confidence(normalized));
  setText("bdPlaceWhy",normalized.why||v34Why(normalized));

  var vibes=byId("bdPlaceVibes");
  if(vibes){
    vibes.innerHTML="";
    v34Vibes(normalized).forEach(function(label){
      var chip=document.createElement("span");
      chip.className="bd-vibe-chip";
      chip.textContent=label;
      vibes.appendChild(chip);
    });
  }

  var saveButton=byId("bdSavePlace");
  if(saveButton){
    var saved=v34IsSaved(normalized);
    saveButton.setAttribute("data-saved",saved?"true":"false");
    saveButton.textContent=saved?"SAVED":"SAVE THIS SPOT";
  }

  return true;
}

function v34SaveSelectedSpot(){
  var place=v34NormalizePlace(v34SelectedSpot||selectedDiscoveryPlace||{});

  if(!validCoordinate(place)){
    toast("Select a marine place first");
    return false;
  }

  if(v34IsSaved(place)){
    toast("This spot is already saved");
    return place;
  }

  place.savedAt=Date.now();
  v34SavedSpots.unshift(place);
  v34SavedSpots=v34SavedSpots.slice(0,200);
  v34PersistCollections();
  v34RenderPlaceCard(place);
  toast("Spot saved to Captain’s Favorites");

  emit("boating:spot-saved",{place:place});
  return place;
}

function v34SetDestination(place){
  var normalized=v34NormalizePlace(place);

  if(!validCoordinate(normalized)){
    toast("This spot does not have a valid coordinate");
    return false;
  }

  ensureTrip();
  state.trip.destination=normalized;
  state.route=null;
  state.returnMode=false;
  state.distanceMiles=null;
  state.etaText="--";
  state.routeConfidence="Bearing Only • Not a Verified Route";

  try{
    sessionStorage.setItem(TRIP_STORAGE_KEY,JSON.stringify(state.trip));
  }catch(error){}

  saveState();
  updateMarkers();
  updateRouteLayers();
  fitActiveTrip();
  render();

  toast("Saved spot set as your return target");
  emit("boating:return-to-saved-spot",{place:normalized});
  return normalized;
}

function v34TakeMeBack(){
  var place=v34SelectedSpot||selectedDiscoveryPlace;

  if(!place){
    toast("Select a spot first");
    return false;
  }

  return v34SetDestination(place);
}

function v34ShareText(place){
  var normalized=v34NormalizePlace(place);
  var coordinateText=
    Number(normalized.lat).toFixed(6)+","+
    Number(normalized.lng).toFixed(6);

  return {
    title:normalized.name||"Meet me here",
    text:"Meet me at "+(normalized.name||"this marine spot")+
      " — "+coordinateText,
    url:"https://www.google.com/maps/search/?api=1&query="+
      encodeURIComponent(coordinateText)
  };
}

function v34Share(place,meetMode){
  var target=v34NormalizePlace(place);

  if(!validCoordinate(target)){
    target=v34NormalizePlace(state.location||{},{
      name:"My Current Marine Location",
      type:"current-location",
      source:"live-location"
    });
  }

  if(!validCoordinate(target)){
    toast("Waiting for a reliable location");
    return Promise.resolve(false);
  }

  var share=v34ShareText(target);
  if(meetMode){
    share.title="Meet me here";
    share.text="Meet me here on the water — "+
      Number(target.lat).toFixed(6)+","+
      Number(target.lng).toFixed(6);
  }

  if(navigator.share){
    return navigator.share(share)
      .then(function(){
        emit("boating:location-shared",{place:target,mode:meetMode?"meet":"place"});
        return true;
      })
      .catch(function(){return false;});
  }

  var copyText=share.text+" "+share.url;

  if(navigator.clipboard&&navigator.clipboard.writeText){
    return navigator.clipboard.writeText(copyText)
      .then(function(){
        toast("Location copied");
        return true;
      })
      .catch(function(){return false;});
  }

  try{
    window.prompt("Copy this location",copyText);
    return Promise.resolve(true);
  }catch(error){
    return Promise.resolve(false);
  }
}

function v34ShowSavedSpots(){
  if(!v34SavedSpots.length){
    toast("No saved spots yet");
    return false;
  }

  clearDiscoveryMarkers();
  paintDiscoveryMarkers(v34SavedSpots,"saved-spots");
  showDiscoveryStatus(v34SavedSpots.length+" saved spots");
  hideDiscoveryStatusLater();
  return true;
}

function v34ManualMarkerElement(){
  var element=document.createElement("div");
  element.className="bd-manual-marker";
  element.innerHTML="<span>📍</span>";
  return element;
}

function v34PaintManualPins(){
  if(!map){return false;}

  v34ManualMarkers.forEach(function(marker){
    try{marker.remove();}catch(error){}
  });
  v34ManualMarkers=[];

  v34ManualPins.forEach(function(place){
    var element=v34ManualMarkerElement();
    var marker=new mapboxgl.Marker({
      element:element,
      anchor:"bottom"
    })
      .setLngLat([place.lng,place.lat])
      .addTo(map);

    element.addEventListener("click",function(){
      showPlaceCard(place);
      v34RenderPlaceCard(place);
    });

    v34ManualMarkers.push(marker);
  });

  return true;
}

function v34CancelDropMode(){
  v34DropMode=false;
  hide(byId("bdDropPinHint"));

  try{
    if(map&&map.getCanvas()){
      map.getCanvas().style.cursor="";
    }
  }catch(error){}

  return true;
}

function v34BeginDropMode(){
  if(!map){
    toast("Map is still loading");
    return false;
  }

  v34DropMode=true;
  show(byId("bdDropPinHint"));

  try{
    map.getCanvas().style.cursor="crosshair";
  }catch(error){}

  toast("Tap the map to drop a pin");
  return true;
}

function v34MapClick(event){
  if(!v34DropMode||!event||!event.lngLat){return;}

  var name=window.prompt("Name this spot","My Marine Spot");
  if(name===null){
    v34CancelDropMode();
    return;
  }

  var place=v34NormalizePlace({
    id:v34Id("manual"),
    name:v34Text(name)||"My Marine Spot",
    address:"Dropped manually on the map",
    type:"manual-pin",
    source:"manual-map-pin",
    lat:event.lngLat.lat,
    lng:event.lngLat.lng,
    confidence:"Your Pin",
    vibes:["Your Pin","Return Later"],
    why:"A personal location you dropped on the map. Confirm conditions, depth, access, and local restrictions before returning."
  });

  v34ManualPins.unshift(place);
  v34ManualPins=v34ManualPins.slice(0,200);
  v34PersistCollections();
  v34PaintManualPins();
  v34CancelDropMode();
  showPlaceCard(place);
  v34RenderPlaceCard(place);
  toast("Pin dropped");

  emit("boating:manual-pin-created",{place:place});
}

function v34RestoreDriveState(){
  var restored=v34Read(DRIVE_STATE_KEY);

  if(!restored||typeof restored!=="object"){return false;}

  if(!state.trip&&restored.trip){
    state.trip=restored.trip;
  }

  if(!state.route&&restored.route){
    state.route=restored.route;
  }

  if(!state.homeBase&&restored.homeBase&&validCoordinate(restored.homeBase)){
    state.homeBase=restored.homeBase;
    ensureTrip();
    state.trip.savedLaunch=restored.homeBase;
    state.trip.launch=restored.homeBase;
  }

  if(Array.isArray(restored.breadcrumb)&&!state.breadcrumb.length){
    state.breadcrumb=restored.breadcrumb.slice(-2000);
  }

  if(restored.cardState==="expanded"||restored.cardState==="compact"){
    state.cardState=restored.cardState;
  }

  state.awarenessActive=!!(
    state.awarenessActive||
    restored.awarenessActive
  );
  state.guidanceActive=!!(
    state.guidanceActive||
    restored.guidanceActive
  );
  state.returnMode=!!(
    state.returnMode||
    restored.returnMode
  );

  return true;
}

function v34PatchDiscoveryQuery(){
  var original=discoveryQuery;

  discoveryQuery=function(type){
    var key=clean(type||"around-me");

    if(key==="hangout"){
      return "sandbar party cove boating hangout swimming area raft up";
    }

    if(key==="saved-spots"){
      return "saved marine spots";
    }

    return original(type);
  };
}

function v34PatchDiscoveryIcon(){
  var original=discoveryIcon;

  discoveryIcon=function(type){
    var key=clean(type).toLowerCase();

    if(key.indexOf("hangout")>=0){return "☀";}
    if(key.indexOf("manual")>=0){return "📍";}
    if(key.indexOf("saved")>=0){return "★";}

    return original(type);
  };
}

function v34PatchNormalize(){
  var original=normalizeDiscoveryPlace;

  normalizeDiscoveryPlace=function(feature,type){
    var place=original(feature,type);
    place.source="mapbox-geocoding";
    place.confidence="Unverified";
    place.vibes=v34Vibes(place);
    place.why=v34Why(place);
    return place;
  };
}

function v34PatchPlaceCard(){
  var original=showPlaceCard;

  showPlaceCard=function(place){
    var result=original(place);
    v34RenderPlaceCard(place);
    return result;
  };
}

function v34PatchExploreHook(){
  var original=exploreNearby;

  exploreNearby=function(type){
    var key=clean(type||"around-me");

    if(key==="saved-spots"){
      return Promise.resolve(v34ShowSavedSpots()?v34SavedSpots:[]);
    }

    return original(key).then(function(items){
      return (items||[]).map(function(item){
        item=v34NormalizePlace(item,{
          type:item&&item.type||key,
          source:item&&item.source||"drive-iq-discovery"
        });
        return item;
      });
    });
  };
}

function v34Bind(){
  var node;

  node=byId("bdSavePlace");
  if(node){node.addEventListener("click",v34SaveSelectedSpot);}

  node=byId("bdTakeMeBack");
  if(node){node.addEventListener("click",v34TakeMeBack);}

  node=byId("bdMeetMeHere");
  if(node){
    node.addEventListener("click",function(){
      v34Share(v34SelectedSpot||selectedDiscoveryPlace,true);
    });
  }

  node=byId("bdSharePlace");
  if(node){
    node.addEventListener("click",function(){
      v34Share(v34SelectedSpot||selectedDiscoveryPlace,false);
    });
  }

  node=byId("bdShareMyLocation");
  if(node){
    node.addEventListener("click",function(){
      v34Share(state.location,true);
    });
  }

  node=byId("bdDropMapPin");
  if(node){node.addEventListener("click",v34BeginDropMode);}

  node=byId("bdCancelDropPin");
  if(node){node.addEventListener("click",v34CancelDropMode);}

  node=byId("bdOpenSavedSpots");
  if(node){node.addEventListener("click",v34ShowSavedSpots);}
}

function v34WaitForMap(){
  var attempts=0;
  var timer=setInterval(function(){
    attempts+=1;

    if(map){
      clearInterval(timer);
      map.on("click",v34MapClick);

      if(map.loaded()){
        v34PaintManualPins();
      }else{
        map.once("load",v34PaintManualPins);
      }

      return;
    }

    if(attempts>100){
      clearInterval(timer);
    }
  },100);
}

v34LoadCollections();
v34RestoreDriveState();
v34PatchDiscoveryQuery();
v34PatchDiscoveryIcon();
v34PatchNormalize();
v34PatchPlaceCard();
v34PatchExploreHook();

if(document.readyState==="loading"){
  document.addEventListener("DOMContentLoaded",function(){
    v34Bind();
    v34RenderSavedSummary();
    v34WaitForMap();
    render();
  },false);
}else{
  v34Bind();
  v34RenderSavedSummary();
  v34WaitForMap();
  render();
}


/* =========================================================
   DRIVE IQ BOATING DRIVE V3.5
   SELECTED WATER HANDOFF + TRUTHFUL SESSION STATE AUTHORITY

   Rules:
   - A selected canonical lake/harbor is the initial map authority.
   - Planning mode never zooms out to include a distant live GPS position.
   - Live GPS may update without stealing the selected-water camera.
   - Discovery scans use the selected water center until the outing is
     actually operating from a marked launch/current-water context.
   - "You are on the water" is shown only when confirmed by explicit context
     or meaningful movement after Home Base is marked.
   - Home Base remains unmarked until the user explicitly saves it.
========================================================= */

var V35_WATER_CENTER_KEY="driveiq.boating.selected-water-center";
var V35_STARTUP_CONTEXT_KEY="driveiq.boating.startup-context";
var v35WaterCenter=null;
var v35StartupContext=null;
var v35CameraAuthority="auto";
var v35LastUserMapGestureAt=0;

function v35ReadSessionJSON(key){
  try{
    return JSON.parse(sessionStorage.getItem(key)||"null");
  }catch(error){
    return null;
  }
}

function v35ValidPoint(value){
  return !!locationFrom(value);
}

function v35NormalizeCenter(value){
  var point=locationFrom(value);
  if(!point){return null;}

  return {
    lat:point.lat,
    lng:point.lng,
    label:clean(value&&value.label||value&&value.name||"Selected water"),
    source:clean(value&&value.source||"boating-start-handoff")
  };
}

function v35ApplyWaterHandoff(){
  var trip=state.trip||{};
  var startup=
    trip.startupContext||
    v35ReadSessionJSON(V35_STARTUP_CONTEXT_KEY)||
    null;

  var center=
    trip.selectedWaterCenter||
    trip.center||
    startup&&startup.selectedWaterCenter||
    v35ReadSessionJSON(V35_WATER_CENTER_KEY)||
    trip.destination||
    null;

  v35WaterCenter=v35NormalizeCenter(center);
  v35StartupContext=startup&&typeof startup==="object"
    ? startup
    : {
        mode:trip.destination?"planned-destination":"current-location",
        onWaterConfirmed:false,
        hasConfirmedLaunch:!!savedLaunch(),
        hasLiveLocation:!!state.location
      };

  if(v35WaterCenter&&trip.destination){
    v35CameraAuthority="selected-water";
  }else{
    v35CameraAuthority="live-location";
  }

  return v35WaterCenter;
}

function v35PlanningMode(){
  return !!(
    v35WaterCenter&&
    destination()&&
    !state.returnMode&&
    !savedLaunch()&&
    !v35OnWaterConfirmed()
  );
}

function v35OnWaterConfirmed(){
  if(
    v35StartupContext&&
    v35StartupContext.onWaterConfirmed===true
  ){
    return true;
  }

  /*
    A marked launch plus actual movement/breadcrumb development is a stronger
    signal than merely opening the page or receiving a stationary GPS fix.
  */
  return !!(
    savedLaunch()&&
    (
      Number(state.speedMph||0)>=3||
      (state.breadcrumb&&state.breadcrumb.length>=4)
    )
  );
}

function v35NearSelectedWater(){
  if(!state.location||!v35WaterCenter){return false;}

  var meters=distanceMeters(state.location,v35WaterCenter);
  return meters!==null&&meters<=3218.688;
}

function v35PrimaryCenter(){
  if(
    v35WaterCenter&&
    (
      v35CameraAuthority==="selected-water"||
      v35PlanningMode()
    )
  ){
    return [v35WaterCenter.lng,v35WaterCenter.lat];
  }

  return (
    coordinate(state.location)||
    coordinate(savedLaunch())||
    coordinate(destination())||
    (
      v35WaterCenter
        ? [v35WaterCenter.lng,v35WaterCenter.lat]
        : null
    )
  );
}

function v35DiscoveryOrigin(){
  if(
    v35WaterCenter&&
    (
      v35PlanningMode()||
      !state.location
    )
  ){
    return locationFrom(v35WaterCenter);
  }

  return locationFrom(
    state.location||
    savedLaunch()||
    v35WaterCenter||
    state.trip&&state.trip.location
  );
}

function v35SessionTitle(){
  if(state.returnMode){
    return "Returning to your launch";
  }

  if(v35OnWaterConfirmed()){
    return "You are on the water";
  }

  if(savedLaunch()){
    return "Launch marked • awareness active";
  }

  if(destination()){
    return "Boating awareness ready";
  }

  if(state.location){
    return "Marine awareness ready";
  }

  return "Preparing marine awareness";
}

function v35SessionSummary(){
  var destinationName=clean(
    destination()&&destination().name||
    v35WaterCenter&&v35WaterCenter.label||
    ""
  );

  if(state.returnMode){
    return "Home Base is active and the way back remains highlighted.";
  }

  if(v35OnWaterConfirmed()){
    return savedLaunch()
      ? "Home Base saved • nearby places and conditions stay visible."
      : "Live position active • mark your launch so the way back stays visible.";
  }

  if(savedLaunch()){
    return "Home Base saved. Start moving when you are ready to begin the outing.";
  }

  if(destinationName){
    return "Centered on "+destinationName+". Mark your launch when you arrive.";
  }

  return "Use your live position and mark your launch before heading out.";
}

function v35ManeuverDistance(){
  if(state.returnMode){
    return formatDistance(returnMetrics().miles);
  }

  if(v35OnWaterConfirmed()){
    return "Position active";
  }

  if(destination()){
    return clean(destination().name||"Destination ready");
  }

  return state.location?"Location ready":"Finding you…";
}

function v35ManeuverInstruction(){
  if(state.returnMode){
    return "Bearing "+headingText(returnMetrics().bearing)+
      " back to your saved launch";
  }

  if(v35OnWaterConfirmed()){
    return "Live position, nearby places, and your saved launch stay visible here.";
  }

  if(destination()){
    return "Planning around "+clean(destination().name||"the selected water")+
      ". Mark Home Base when you launch.";
  }

  return "Live position and marine conditions will stay visible here.";
}

function v35RenderTruth(){
  setText("bdDestinationName",v35SessionTitle());
  setText("bdTripSummary",v35SessionSummary());
  setText("bdManeuverDistance",v35ManeuverDistance());
  setText("bdManeuverInstruction",v35ManeuverInstruction());

  setText(
    "bdRouteConfidence",
    state.returnMode
      ? "Return Active"
      : v35OnWaterConfirmed()
        ? "On-Water Active"
        : destination()
          ? "Planning Ready"
          : state.awarenessActive
            ? "Awareness Active"
            : "Awareness Ready"
  );
}

function v35CenterSelectedWater(options){
  if(!map||!v35WaterCenter){return false;}

  var supplied=options||{};
  map.easeTo({
    center:[v35WaterCenter.lng,v35WaterCenter.lat],
    zoom:Number(supplied.zoom||13.3),
    pitch:Number(supplied.pitch||46),
    bearing:0,
    duration:Number(supplied.duration||700)
  });

  return true;
}

function v35FitAuthority(){
  if(!map){return false;}

  if(v35PlanningMode()){
    return v35CenterSelectedWater({
      zoom:13.3,
      duration:650
    });
  }

  return false;
}

function v35BindMapAuthority(){
  if(!map||map.__bdV35AuthorityBound){return;}
  map.__bdV35AuthorityBound=true;

  function userGesture(){
    v35LastUserMapGestureAt=Date.now();
    v35CameraAuthority="user";
  }

  map.on("dragstart",userGesture);
  map.on("zoomstart",function(event){
    if(event&&event.originalEvent){userGesture();}
  });
  map.on("rotatestart",userGesture);
  map.on("pitchstart",userGesture);
}

function v35MarkLaunchContext(){
  if(!state.trip){return;}

  if(!state.trip.startupContext){
    state.trip.startupContext={};
  }

  state.trip.startupContext.hasConfirmedLaunch=true;
  state.trip.startupContext.launchMarkedAt=Date.now();
  v35StartupContext=state.trip.startupContext;
}

function v35UpdateMovementContext(){
  if(!state.trip){return;}

  if(!state.trip.startupContext){
    state.trip.startupContext={};
  }

  if(v35OnWaterConfirmed()){
    state.trip.startupContext.onWaterConfirmed=true;
    state.trip.startupContext.onWaterConfirmedAt=
      state.trip.startupContext.onWaterConfirmedAt||Date.now();
    v35StartupContext=state.trip.startupContext;
  }
}

/* Integrate selected-water authority into existing private functions. */
var v35OriginalInitializeMap=initializeMap;
initializeMap=function(){
  var originalOrigin=currentOriginCoordinate;
  currentOriginCoordinate=function(){
    return v35PrimaryCenter()||originalOrigin();
  };

  return v35OriginalInitializeMap().then(function(result){
    currentOriginCoordinate=originalOrigin;
    v35BindMapAuthority();

    if(v35PlanningMode()){
      v35CenterSelectedWater({
        zoom:13.3,
        duration:0
      });
    }

    return result;
  }).catch(function(error){
    currentOriginCoordinate=originalOrigin;
    state.lastError=clean(error&&error.message||error);
    return false;
  });
};

var v35OriginalFitActiveTrip=fitActiveTrip;
fitActiveTrip=function(){
  if(v35FitAuthority()){return true;}
  return v35OriginalFitActiveTrip();
};

var v35OriginalCenterMap=centerMap;
centerMap=function(){
  if(
    v35WaterCenter&&
    (
      v35PlanningMode()||
      v35CameraAuthority==="selected-water"
    )
  ){
    v35CameraAuthority="selected-water";
    return v35CenterSelectedWater({
      zoom:13.8,
      duration:650
    });
  }

  return v35OriginalCenterMap();
};

var v35OriginalSearchDiscovery=searchDiscovery;
searchDiscovery=function(type){
  var previousLocation=state.location;
  var origin=v35DiscoveryOrigin();

  if(origin&&v35PlanningMode()){
    state.location=origin;
  }

  return v35OriginalSearchDiscovery(type)
    .then(function(results){
      state.location=previousLocation;
      return results;
    })
    .catch(function(error){
      state.location=previousLocation;
      state.lastError=clean(error&&error.message||error);
      return [];
    });
};

var v35OriginalRender=render;
render=function(){
  var result=v35OriginalRender();
  v35RenderTruth();
  return result;
};

var v35OriginalMarkHomeBase=markHomeBase;
markHomeBase=function(options){
  var result=v35OriginalMarkHomeBase(options);

  if(result){
    v35MarkLaunchContext();
    v35CameraAuthority="live-location";
    saveState();
    v35RenderTruth();
  }

  return result;
};

var v35OriginalUpdateLocation=updateLocation;
updateLocation=function(position){
  var result=v35OriginalUpdateLocation(position);

  if(result){
    v35UpdateMovementContext();

    /*
      GPS updates may update the vessel marker, but they may not pull the
      camera away from the selected lake while still in planning mode.
    */
    if(
      v35PlanningMode()&&
      v35CameraAuthority!=="user"&&
      Date.now()-v35LastUserMapGestureAt>3000
    ){
      v35CameraAuthority="selected-water";
    }

    v35RenderTruth();
  }

  return result;
};

var v35OriginalRouteContext=routeContext;
routeContext=function(){
  var context=v35OriginalRouteContext();
  context.selectedWaterCenter=v35WaterCenter;
  context.startupContext=v35StartupContext;
  context.sessionPhase=
    state.returnMode
      ? "returning"
      : v35OnWaterConfirmed()
        ? "on-water"
        : savedLaunch()
          ? "launch-marked"
          : destination()
            ? "planning"
            : "location-ready";
  return context;
};




/* =========================================================
   DRIVE IQ BOATING DRIVE V4
   APPROVED MOCKUP COMMAND-CENTER AUTHORITY

   Wires the approved V4 interface while preserving:
   - selected-water planning authority
   - explicit Home Base behavior
   - truthful planning / launch-marked / on-water / returning states
   - bearing-only safety language
   - discovery, saved spots, sharing, and manual pins
========================================================= */

function v4Conditions(){
  return state.trip&&state.trip.conditions||{};
}

function v4WindText(){
  var conditions=v4Conditions();

  if(conditions.windMph!=null){
    return Math.round(Number(conditions.windMph))+" mph";
  }

  return "Checking";
}

function v4Phase(){
  if(state.returnMode){return "returning";}
  if(v35OnWaterConfirmed()){return "on-water";}
  if(savedLaunch()){return "launch-marked";}
  if(destination()){return "planning";}
  return state.location?"location-ready":"locating";
}

function v4PhaseLabel(){
  var phase=v4Phase();

  if(phase==="returning"){return "Return Active";}
  if(phase==="on-water"){return "On-Water Active";}
  if(phase==="launch-marked"){return "Launch Marked";}
  if(phase==="planning"){return "Planning Ready";}
  if(phase==="location-ready"){return "Awareness Ready";}
  return "Locating";
}

function v4RenderCenterCluster(){
  var cluster=document.querySelector(".bd-center-cluster");
  var label=cluster&&cluster.querySelector(".bd-center-label");
  var phase=v4Phase();

  if(!cluster){return;}

  /*
    The real Mapbox vessel marker remains the live position authority.
    The center cluster is only a visual planning/focus cue.
  */
  if(phase==="planning"&&v35WaterCenter){
    cluster.style.display="";
    if(label){
      label.textContent=clean(
        v35WaterCenter.label||
        destination()&&destination().name||
        "Selected Water Center"
      );
    }
  }else{
    cluster.style.display="none";
  }
}

function v4RenderCommandCenter(){
  var home=savedLaunch();
  var phase=v4Phase();
  var returnButton=byId("bdReturnToLaunch");
  var launchButton=byId("bdMarkHomeBase");
  var startButton=byId("bdStartGuidance");

  setText("bdWindStatus",v4WindText());
  setText("bdRouteConfidence",v4PhaseLabel());

  if(launchButton){
    launchButton.textContent=home
      ? "HOME BASE SAVED"
      : "MARK MY LAUNCH";
    launchButton.setAttribute(
      "data-active",
      home?"true":"false"
    );
  }

  if(returnButton){
    returnButton.textContent=state.returnMode
      ? "RETURN ACTIVE"
      : "RETURN TO LAUNCH";
    returnButton.setAttribute(
      "data-active",
      state.returnMode?"true":"false"
    );
  }

  if(startButton){
    startButton.textContent=state.awarenessActive
      ? "PAUSE AWARENESS"
      : "START AWARENESS";
  }

  var mapHome=byId("bdDropHomePinMap");
  if(mapHome){
    mapHome.textContent=home
      ? "📍 HOME BASE SAVED"
      : "📍 DROP HOME PIN";
  }

  var tripCard=byId("bdTripCard");
  if(tripCard){
    tripCard.setAttribute("data-phase",phase);
  }

  v4RenderCenterCluster();
}

function v4OpenDiscovery(){
  setCardState("expanded");
  return exploreNearby("around-me");
}

var v4OriginalRender=render;
render=function(){
  var result=v4OriginalRender();
  v4RenderCommandCenter();
  return result;
};

var v4OriginalStartAwareness=startAwareness;
startAwareness=function(){
  var result=v4OriginalStartAwareness();

  if(result){
    setCardState("compact");
  }

  v4RenderCommandCenter();
  return result;
};

var v4OriginalReturnToLaunch=returnToLaunch;
returnToLaunch=function(){
  var result=v4OriginalReturnToLaunch();

  if(result){
    setCardState("compact");
  }

  v4RenderCommandCenter();
  return result;
};

var v4OriginalMarkHomeBase=markHomeBase;
markHomeBase=function(options){
  var result=v4OriginalMarkHomeBase(options);
  v4RenderCommandCenter();
  return result;
};

var v4OriginalClearHomeBase=clearHomeBase;
clearHomeBase=function(){
  var result=v4OriginalClearHomeBase();
  v4RenderCommandCenter();
  return result;
};


window.DriveIQBoatingDrive={
  init:init,
  buildRoute:buildRoute,
  refreshRoute:refreshRoute,
  refreshIntelligence:refreshIntelligence,
  applyRoute:applyRoute,
  updateLocation:updateLocation,
  centerMap:centerMap,
  setCardState:setCardState,
  toggleCard:toggleCard,
  openIntel:openIntel,
  startAwareness:startAwareness,
  returnToLaunch:returnToLaunch,
  markHomeBase:markHomeBase,
  replaceHomeBase:replaceHomeBase,
  renameHomeBase:renameHomeBase,
  moveHomeBase:moveHomeBase,
  clearHomeBase:clearHomeBase,
  evaluateLaunchDetection:evaluateLaunchDetection,
  confirmLaunchPrompt:confirmLaunchPrompt,
  dismissLaunchPrompt:dismissLaunchPrompt,
  exploreNearby:exploreNearby,
  searchDiscovery:searchDiscovery,
  clearDiscoveryMarkers:clearDiscoveryMarkers,
  addStop:addStop,
  endTrip:endTrip,
  saveSelectedSpot:v34SaveSelectedSpot,
  showSavedSpots:v34ShowSavedSpots,
  beginDropPin:v34BeginDropMode,
  cancelDropPin:v34CancelDropMode,
  takeMeBack:v34TakeMeBack,
  sharePlace:function(place){return v34Share(place,false);},
  meetMeHere:function(place){return v34Share(place||state.location,true);},
  savedSpots:function(){return v34SavedSpots.slice();},
  manualPins:function(){return v34ManualPins.slice();},
  selectedWaterCenter:function(){return v35WaterCenter;},
  sessionPhase:v4Phase,
  commandCenterPhase:v4Phase,
  state:function(){return state;},
  map:function(){return map;},
  build:BUILD
};

if(document.readyState==="loading"){
  document.addEventListener("DOMContentLoaded",init,false);
}else{
  init();
}

})(window,document);
